import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { IndicatorsDocument } from 'lib/api/collections/types';
import CustomButton from 'lib/designSystem/Button';
import { CentralPieChartItem } from 'lib/designSystem/Charts/CentralPieChart';
import ColoredCircle from 'lib/designSystem/ColoredCircle';
import EnhancedTable from 'lib/designSystem/Table';
import { CHART_COLORS } from 'lib/functions/const/colors';
import React from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

type AmountsDialogProps = {
  openDialog: boolean;
  handleDialogClose: () => void;
  documentsForInsights: IndicatorsDocument[];
  selectedSegment: CentralPieChartItem;
  showDueBy: string;
};

const AmountsDialog = ({
  openDialog,
  handleDialogClose,
  documentsForInsights,
  selectedSegment,
  showDueBy,
}: AmountsDialogProps) => {
  const dispatch = useDispatch();

  const sortedDocumentsForInsights = documentsForInsights.sort((a, b) =>
    showDueBy === 'business'
      ? b.documentRemainingAmountNet - a.documentRemainingAmountNet
      : b.documentRemainingAmount - a.documentRemainingAmount,
  );

  const handleShowBusinessDetails = (id: string) => {
    const row = documentsForInsights.find(
      (x: IndicatorsDocument) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row = documentsForInsights.find(
      (x: IndicatorsDocument) => x.id === id,
    );
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier ?? '',
        name: row.stakeholderName,
      }),
    );
  };

  return (
    <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="lg">
      <DialogTitle>
        <ColoredCircle
          color={selectedSegment?.color ?? CHART_COLORS.red}
          size={15}
        />
        {` ${selectedSegment?.name}`}
      </DialogTitle>
      <DialogContent>
        <EnhancedTable
          dataHeaders={[
            { key: 'documentTypeEsp', label: 'Tipo' },
            { key: 'collectorName', label: 'Ejecutivo' },
            { key: 'folio', label: 'Folio' },
            {
              key: 'businessName',
              label: 'Cliente',
              handleAction: handleShowBusinessDetails,
              icon: <Info fontSize="small" />,
            },
            {
              key: 'stakeholderName',
              label: 'Pagador',
              handleAction: handleShowStakeholderDetails,
              icon: <Info fontSize="small" />,
            },
            {
              key: 'paymentDate',
              label: 'Vencimiento',
              type: 'date',
            },
            ...(showDueBy === 'business'
              ? [
                  {
                    key: 'documentRemainingAmountNet',
                    label: 'Saldo financiado',
                    type: 'money',
                  },
                ]
              : [
                  {
                    key: 'documentRemainingAmount',
                    label: 'Saldo documento',
                    type: 'money',
                  },
                ]),
          ]}
          data={sortedDocumentsForInsights}
          finalSumRow
          exportToFile="xlsx"
        />
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleDialogClose}>Cerrar</CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AmountsDialog;
